import { assertGSplatGpuPickingRequirements } from '@/plugins/gsplat/GSplatCapabilityRequirements'
import type {
  GSplatGpuPickCoordinator,
  GSplatPickSample,
  GSplatSystemAssetSnapshot,
  GSplatSystemConfiguration,
  SplatDataCpuPicker,
} from '@/plugins/gsplat/GSplatSystem'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatAssetStore } from '@/plugins/gsplat/system/GSplatAssetStore'
import type { PhaseRenderContext } from '@/shared/types/assets'
import type { AssetId, PickQuery, PickResult, RectPickQuery } from '@/shared/types/core'
import type { ViewFrameContext, ViewId } from '@/shared/types/view'

/** 对应旧 GSplatPickManager；请求、快照、失效与结果映射由 System 域拥有。 */
export class GSplatPickingService {
  constructor(
    private readonly store: GSplatAssetStore,
    private readonly configuration: GSplatSystemConfiguration,
    private readonly adapter: Pick<GSplatGraphicsAdapter, 'backend'>,
    private readonly cpuPicker: SplatDataCpuPicker,
    private readonly gpuCoordinator: GSplatGpuPickCoordinator
  ) {}

  pickCpu(query: PickQuery): PickResult | null {
    return this.mapSample(query, this.cpuPicker.pick(query, this.store.snapshot()))
  }

  pick(query: PickQuery): PickResult | null {
    const assets = this.store.snapshot()
    const hasPrewarm = query.prewarmGpu && this.gpuCoordinator.prewarm !== undefined
    if (hasPrewarm) this.gpuCoordinator.prewarm!(query, assets)
    // A coordinator that owns prewarm explicitly must not turn the synchronous hint into a real
    // GPU readback.  It may still fall through to the existing CPU result when gpuOnly is false.
    if (!hasPrewarm && this.gpuCoordinator.pickSync) {
      // 同步 GPU picker 的 null 表示一次权威的未命中，而不是“GPU 尚不可用”。若继续回退
      // CPU，会在用户点击高斯外部时逐个遍历全部 splat，造成秒级主线程阻塞。
      return this.mapSample(query, this.gpuCoordinator.pickSync(query, assets))
    }
    if (query.gpuOnly) return null
    return this.mapSample(query, this.cpuPicker.pick(query, assets))
  }

  pickRect(query: RectPickQuery): PickResult[] {
    const assets = this.store.snapshot()
    const samples =
      this.gpuCoordinator.pickRectSync?.(query, assets) ??
      (query.gpuOnly ? [] : (this.cpuPicker.pickRect?.(query, assets) ?? []))
    return samples
      .map((sample) => this.mapRectSample(query, sample))
      .filter((result): result is PickResult => result !== null)
  }

  async pickGpu(query: PickQuery): Promise<PickResult | null> {
    assertGSplatGpuPickingRequirements(this.configuration.capabilities, this.adapter.backend)
    const sample = await this.gpuCoordinator.request(query, this.store.snapshot())
    return this.mapSample(query, sample)
  }

  async pickRectGpu(query: RectPickQuery): Promise<PickResult[]> {
    assertGSplatGpuPickingRequirements(this.configuration.capabilities, this.adapter.backend)
    if (!this.gpuCoordinator.requestRect) {
      if (query.gpuOnly) return []
      const samples = this.cpuPicker.pickRect?.(query, this.store.snapshot()) ?? []
      return samples
        .map((sample) => this.mapRectSample(query, sample))
        .filter((result): result is PickResult => result !== null)
    }
    const samples = await this.gpuCoordinator.requestRect(query, this.store.snapshot())
    return samples
      .map((sample) => this.mapRectSample(query, sample))
      .filter((result): result is PickResult => result !== null)
  }

  prepareView(
    frame: ViewFrameContext,
    assets: readonly GSplatSystemAssetSnapshot[],
    generation: number
  ): void {
    this.gpuCoordinator.prepareView(frame, assets, Object.freeze({ generation }))
  }

  renderView(
    frame: ViewFrameContext,
    context: PhaseRenderContext,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): void {
    this.gpuCoordinator.renderView?.(frame, context, assets)
  }

  cancelAsset(id: AssetId, generation: number): void {
    this.gpuCoordinator.cancelAsset(id, generation)
  }

  cancelView(viewId: ViewId, generation: number): void {
    this.gpuCoordinator.cancelView(viewId, generation)
  }

  dispose(): void {
    let firstError: unknown
    try {
      this.gpuCoordinator.dispose()
    } catch (error) {
      firstError = error
    }
    try {
      this.cpuPicker.dispose()
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  private mapSample(query: PickQuery, sample: GSplatPickSample | null): PickResult | null {
    if (!sample) return null
    const record = this.store.get(sample.assetId)
    if (
      !record ||
      record.binding.generation !== sample.resourceGeneration ||
      !record.binding.mounted ||
      !record.binding.handle.visible
    ) {
      return null
    }
    return {
      assetId: sample.assetId,
      kind: 'gaussian-splat',
      point: sample.point.clone(),
      distance: sample.distance ?? query.ray.origin.distanceTo(sample.point),
      normal: sample.normal?.clone(),
      _raw: sample.raw,
    }
  }

  private mapRectSample(query: RectPickQuery, sample: GSplatPickSample): PickResult | null {
    const record = this.store.get(sample.assetId)
    if (
      !record ||
      record.binding.generation !== sample.resourceGeneration ||
      !record.binding.mounted ||
      !record.binding.handle.visible
    ) {
      return null
    }
    return {
      assetId: sample.assetId,
      kind: 'gaussian-splat',
      point: sample.point.clone(),
      distance: sample.distance ?? query.camera.position.distanceTo(sample.point),
      normal: sample.normal?.clone(),
      _raw: sample.raw,
    }
  }
}
